import { useEffect, useRef } from 'react';

interface UseAutoLocateFilteredPageOptions {
  hasActiveFilters: boolean;
  filterSignature: string;
  filteredCount: number;
  pageItemCount: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

/**
 * When column filters are active on a server-paginated table, scan forward until
 * we find the first page containing filtered matches. This avoids forcing users
 * to manually step through pages to find where a filtered row lives.
 */
export function useAutoLocateFilteredPage({
  hasActiveFilters,
  filterSignature,
  filteredCount,
  pageItemCount,
  page,
  totalPages,
  isLoading,
  onPageChange,
}: UseAutoLocateFilteredPageOptions): void {
  const exhaustedFilterSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    exhaustedFilterSignatureRef.current = null;
  }, [filterSignature]);

  useEffect(() => {
    if (!hasActiveFilters) {
      exhaustedFilterSignatureRef.current = null;
      return;
    }

    if (isLoading || pageItemCount === 0 || filteredCount > 0 || totalPages <= 1) {
      return;
    }

    if (exhaustedFilterSignatureRef.current === filterSignature) {
      return;
    }

    if (page < totalPages - 1) {
      onPageChange(page + 1);
      return;
    }

    exhaustedFilterSignatureRef.current = filterSignature;
    if (page !== 0) {
      onPageChange(0);
    }
  }, [
    hasActiveFilters,
    filterSignature,
    filteredCount,
    isLoading,
    onPageChange,
    page,
    pageItemCount,
    totalPages,
  ]);
}
